import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
}

const SEO = ({
  title = "Nexasoft Infotech - Leading Software Development Company | Web, Mobile & AI Solutions",
  description = "Nexasoft Infotech is a premier software development company specializing in modern web development, mobile apps, AI/ML solutions, cloud-native applications, and digital transformation.",
  keywords = "software development company, web development, mobile app development, React.js, Next.js, Node.js, AI ML solutions, cloud-native applications",
  image = "/logo.png",
  url,
  type = "website",
  article,
}: SEOProps) => {
  const location = useLocation();
  const currentUrl = url || `https://nexasoftinfotech.com${location.pathname}`;
  const fullImageUrl = image.startsWith("http") ? image : `https://nexasoftinfotech.com${image}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, attribute: string = "name") => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Update or create property meta tags
    const updatePropertyMetaTag = (property: string, content: string) => {
      updateMetaTag(property, content, "property");
    };

    // Primary Meta Tags
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords);
    updateMetaTag("title", title);

    // Open Graph Tags
    updatePropertyMetaTag("og:title", title);
    updatePropertyMetaTag("og:description", description);
    updatePropertyMetaTag("og:image", fullImageUrl);
    updatePropertyMetaTag("og:url", currentUrl);
    updatePropertyMetaTag("og:type", type);

    // Twitter Card Tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", fullImageUrl);

    // Canonical URL
    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", currentUrl);

    // Article specific meta tags
    if (article && type === "article") {
      if (article.publishedTime) {
        updatePropertyMetaTag("article:published_time", article.publishedTime);
      }
      if (article.modifiedTime) {
        updatePropertyMetaTag("article:modified_time", article.modifiedTime);
      }
      if (article.author) {
        updatePropertyMetaTag("article:author", article.author);
      }
      if (article.tags) {
        article.tags.forEach((tag, index) => {
          updatePropertyMetaTag(`article:tag`, tag);
        });
      }
    }
  }, [title, description, keywords, image, currentUrl, type, article]);

  return null;
};

export default SEO;

